/**
 * DataGrid Helper Utilities
 * 
 * This file contains utility functions for safely working with DataGrid components
 * to prevent common runtime errors related to null/undefined values.
 */

import { GridColDef } from '@mui/x-data-grid';

// Define our own value getter/formatter parameter types based on MUI Data Grid v7
interface GridValueGetterParams {
  value?: any;
  row: any;
  field: string;
  api: any;
  id: string | number;
}

interface GridValueFormatterParams {
  value?: any;
  field: string;
  api: any;
  id: string | number;
}

/**
 * Adds safety checks to all valueGetter and valueFormatter functions
 * in a grid column definition to prevent "Cannot read properties of undefined" errors
 * 
 * @param columns Array of column definitions
 * @returns The same array with safety checks added
 */
export const makeSafeGridColumns = (columns: GridColDef[]): GridColDef[] => {
  return columns.map(column => {
    const safeColumn = { ...column };
    
    // Add safety for valueGetter functions
    if (typeof safeColumn.valueGetter === 'function') {
      const originalValueGetter = safeColumn.valueGetter;
      // @ts-ignore - Ignore the argument count mismatch
      safeColumn.valueGetter = (params: any) => {
        if (!params) return null;
        if (!params.row) return null;
        
        try {
          // @ts-ignore - Ignore the argument count mismatch
          return originalValueGetter(params);
        } catch (error) {
          console.error(`Error in valueGetter for column ${column.field}:`, error);
          return null;
        }
      };
    }
    
    // Add safety for valueFormatter functions
    if (typeof safeColumn.valueFormatter === 'function') {
      const originalValueFormatter = safeColumn.valueFormatter;
      // @ts-ignore - Ignore the argument count mismatch
      safeColumn.valueFormatter = (params: any) => {
        if (!params) return '';
        if (params.value === undefined || params.value === null) return '';
        
        try {
          // @ts-ignore - Ignore the argument count mismatch
          return originalValueFormatter(params);
        } catch (error) {
          console.error(`Error in valueFormatter for column ${column.field}:`, error);
          return '';
        }
      };
    }
    
    return safeColumn;
  });
};

/**
 * Creates a safe valueGetter that handles null/undefined params and row
 * Use this as a wrapper for your valueGetter functions
 */
export const safeValueGetter = (fn: (params: any) => any) => (params: any) => {
  if (!params || !params.row) return null;
  try {
    return fn(params);
  } catch (error) {
    console.error('Error in valueGetter:', error);
    return null;
  }
};

/**
 * Creates a safe valueFormatter that handles null/undefined params and value
 * Use this as a wrapper for your valueFormatter functions
 */
export const safeValueFormatter = (fn: (params: any) => any) => (params: any) => {
  if (!params || params.value === undefined || params.value === null) return '';
  try {
    return fn(params);
  } catch (error) {
    console.error('Error in valueFormatter:', error);
    return '';
  }
}; 